import React, { createContext, useContext } from 'react';

import { OcChatConfig, PreviewState } from '../../../types/oc';
import { useOcFabWidgetOptions } from '../contexts/OcFabWidgetOptionsContext';
import { SessionManager, usePreviewSessionManager, useSessionManager } from './useOcSessionManager';

const SessionManagerContext = createContext<SessionManager | null>(null);
SessionManagerContext.displayName = 'SessionManagerContext';

export const useSessionManagerContext = (): SessionManager => {
    const context = useContext(SessionManagerContext);
    if (!context) {
        throw new Error('useSessionManagerContext must be used within SessionManagerProvider');
    }
    return context;
};

export interface SessionManagerProviderProps {
    children: React.ReactNode;
    config: OcChatConfig;
    originToolId: string;
    isAuthenticated: boolean;
    previewState?: PreviewState;
}

export const SessionManagerProvider: React.FC<SessionManagerProviderProps> = ({
    children,
    config,
    originToolId,
    isAuthenticated,
    previewState,
}) => {
    // Always call hooks unconditionally to satisfy React hooks rules
    // Then select which one to use based on mode
    // Provide a default previewState if not provided to satisfy hook call
    const defaultPreviewState: PreviewState = {
        authState: 'authenticated',
        messages: [],
        sessionHistory: [],
    };
    const {showChatHistory} = useOcFabWidgetOptions();
    const previewSessionManager = usePreviewSessionManager(showChatHistory, previewState || defaultPreviewState);
    const productionSessionManager = useSessionManager(showChatHistory, config, originToolId, isAuthenticated);
    
    // Use preview session manager if in preview mode, otherwise use production session manager
    const sessionManager = config.mode === 'preview' && previewState
        ? previewSessionManager
        : productionSessionManager;

    return <SessionManagerContext.Provider value={sessionManager}>{children}</SessionManagerContext.Provider>;
};

